import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Outlet, useLocation, Link, Navigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import {
  Menu,
  X,
  Home,
  LogOut,
  MapPin,
  ArrowRightFromLine,
  ArrowRightToLine,
  UserRound,
} from 'lucide-react';
import {
  isPortalSessionInvalidating,
  notifyTenantPortalAuthFailure,
} from '../../services/tenantPortalSession';
import LoginPage from '../../pages/LoginPage';
import logoFull from '../../assets/images/logo.svg';
import logoMark from '../../assets/images/logo-mark.svg';
import { usePageProgressByHref } from '../../contexts/ProspectPageProgressContext';
import { ProspectPageProgressProvider } from '../../contexts/ProspectPageProgressContext';
import {
  ImpersonationProfileProvider,
  TenantProfileProvider,
  useTenantProfile,
} from '../../contexts/TenantProfileContext';
import {
  clearAssistedLoginSessionToken,
  readAssistedLoginSessionToken,
} from '../../services/assistedLoginSession';
import SidebarProgressRing from './SidebarProgressRing';
import ProspectSidebarProgress from './ProspectSidebarProgress';
import PortalSessionHeartbeat from './PortalSessionHeartbeat';
import WebsiteHiveProspectIntentSync from './WebsiteHiveProspectIntentSync';
import { PROSPECT_PORTAL_PATHS, PROSPECT_PORTAL_STEPS } from '../../constants/prospectPortalSteps';
import ProspectDesktopLinkCard from './ProspectDesktopLinkCard';
import ProspectNextSectionButton from './ProspectNextSectionButton';
import { TenantAppearanceToggle } from './TenantAppearanceToggle';
import { TenantAppearanceDock } from './TenantAppearanceDock';

interface NavItem {
  name: string;
  href: string;
  icon: typeof Home;
  stepLabel?: string;
}

function cn(...classes: (string | boolean | undefined)[]) {
  return classes.filter(Boolean).join(' ');
}

function SidebarContent({
  currentPath,
  onLogout,
  onNavClick,
  themeOptions = 'full',
}: {
  currentPath: string;
  onLogout: () => void;
  onNavClick?: () => void;
  themeOptions?: 'light-dark' | 'full';
}) {
  const { tenant, prospect, applicant, kind } = useTenantProfile();
  const pageProgressByHref = usePageProgressByHref();

  const displayName = tenant
    ? [tenant.firstName, tenant.lastName].filter(Boolean).join(' ') || 'Tenant'
    : prospect?.displayName || applicant?.displayName || null;

  const residentialLease = tenant?.residentialLease;
  const propertyLabel = residentialLease?.propertyName
    ? residentialLease.propertyUnitCount > 1 && residentialLease.unitNumber
      ? `#${residentialLease.unitNumber} - ${residentialLease.propertyName}`
      : residentialLease.propertyName
    : null;

  const homeHref = kind === 'tenant' ? '/' : '/my-information';

  const navigation = useMemo<NavItem[]>(() => {
    const items: NavItem[] =
      kind === 'prospect'
        ? PROSPECT_PORTAL_STEPS.map(({ name, href, icon, stepLabel }) => ({
            name,
            href,
            icon,
            stepLabel,
          }))
        : [{ name: 'My Information', href: '/my-information', icon: UserRound }];
    if (tenant?.moveIn != null) {
      items.push({ name: 'Move In', href: '/move-in', icon: ArrowRightToLine });
    }
    if (tenant?.moveOut != null) {
      items.push({ name: 'Move Out', href: '/move-out', icon: ArrowRightFromLine });
    }
    return items;
  }, [kind, tenant?.moveOut, tenant?.moveIn]);

  return (
    <>
      <div className="flex h-16 shrink-0 items-center px-2">
        <Link to={homeHref} onClick={onNavClick}>
          <img src={logoFull} alt="Amber Peak" className="h-7 w-auto" />
        </Link>
      </div>

      <nav className="flex flex-1 flex-col">
        <ul role="list" className="flex flex-1 flex-col gap-y-7">
          <li>
            {kind === 'prospect' && (
              <p className="mb-2 px-2 text-xs font-medium text-gray-400 dark:text-gray-500">Your next steps</p>
            )}
            <ul role="list" className="-mx-2 space-y-1">
              {navigation.map((item) => {
                const isCurrent = currentPath === item.href;
                const Icon = item.icon;
                const progress = kind === 'prospect' ? pageProgressByHref[item.href] ?? null : null;
                return (
                  <li key={item.name}>
                    <Link
                      to={item.href}
                      onClick={onNavClick}
                      className={cn(
                        isCurrent
                          ? 'bg-red-50 dark:bg-red-950/40 text-red-700 dark:bg-red-900/20 dark:text-red-400'
                          : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 hover:text-red-700 dark:text-gray-400 dark:hover:bg-white/5 dark:hover:text-red-400',
                        'group flex items-center gap-3 rounded-md px-2 py-2.5 text-sm font-semibold',
                      )}
                    >
                      {progress ? (
                        <SidebarProgressRing completed={progress.completed} total={progress.total}>
                          <Icon
                            size={18}
                            className={cn(
                              isCurrent
                                ? 'text-red-700 dark:text-red-400'
                                : 'text-gray-400 dark:text-gray-500 group-hover:text-red-700 dark:group-hover:text-red-400',
                            )}
                          />
                        </SidebarProgressRing>
                      ) : (
                        <span
                          className="flex h-10 w-10 shrink-0 items-center justify-center"
                          aria-hidden="true"
                        >
                          <Icon
                            size={22}
                            className={cn(
                              isCurrent
                                ? 'text-red-700 dark:text-red-400'
                                : 'text-gray-400 dark:text-gray-500 group-hover:text-red-700 dark:group-hover:text-red-400',
                            )}
                          />
                        </span>
                      )}
                      <span className="min-w-0 flex flex-1 flex-col justify-center">
                        {item.stepLabel && (
                          <span
                            className={cn(
                              'text-[11px] font-medium uppercase tracking-wide leading-4',
                              isCurrent ? 'text-red-500 dark:text-red-400' : 'text-gray-400 group-hover:text-red-400 dark:group-hover:text-red-300 dark:text-gray-500',
                            )}
                          >
                            {item.stepLabel}
                          </span>
                        )}
                        <span className="leading-5">{item.name}</span>
                        {progress ? (
                          <span className="sr-only">
                            {progress.completed} of {progress.total} sections complete
                          </span>
                        ) : null}
                      </span>
                    </Link>
                  </li>
                );
              })}
            </ul>
          </li>

          <li className="-mx-6 mt-auto">
            <TenantAppearanceToggle themeOptions={themeOptions} />
            <div className="border-t border-gray-100 px-6 py-4 dark:border-white/5">
              {displayName && (
                <p className="text-sm font-semibold text-gray-900 truncate dark:text-white">{displayName}</p>
              )}
              {propertyLabel && (
                <div className="flex items-center gap-1.5 mt-1">
                  <MapPin size={13} className="text-gray-400 dark:text-gray-500 shrink-0" />
                  <span className="text-xs text-gray-500 truncate dark:text-gray-400">{propertyLabel}</span>
                </div>
              )}
              <button
                onClick={onLogout}
                className="flex items-center gap-2 mt-3 text-sm text-gray-500 hover:text-red-700 transition-colors dark:text-gray-400 dark:hover:text-red-400"
              >
                <LogOut size={16} />
                Sign out
              </button>
            </div>
          </li>
        </ul>
      </nav>
    </>
  );
}

function MainLayoutInner({ sessionToken }: { sessionToken?: string }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const drawerRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const { logout } = useAuth0();

  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  const closeSidebar = useCallback(() => setSidebarOpen(false), []);

  useEffect(() => {
    if (!sidebarOpen) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') closeSidebar();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [sidebarOpen, closeSidebar]);

  const handleLogout = () => {
    if (sessionToken) {
      clearAssistedLoginSessionToken();
      window.location.assign('/');
      return;
    }
    logout({ logoutParams: { returnTo: window.location.origin } });
  };

  const tree = (
    <ProspectPageProgressProvider>
      <div className="flex h-full min-h-0 flex-col">
        <LayoutContent
          sidebarOpen={sidebarOpen}
          setSidebarOpen={setSidebarOpen}
          closeSidebar={closeSidebar}
          location={location}
          onLogout={handleLogout}
        />
      </div>
    </ProspectPageProgressProvider>
  );

  if (sessionToken) {
    return <ImpersonationProfileProvider token={sessionToken}>{tree}</ImpersonationProfileProvider>;
  }

  return <TenantProfileProvider>{tree}</TenantProfileProvider>;
}

function LayoutContent({
  sidebarOpen,
  setSidebarOpen,
  closeSidebar,
  location,
  onLogout,
}: {
  sidebarOpen: boolean;
  setSidebarOpen: (open: boolean) => void;
  closeSidebar: () => void;
  location: ReturnType<typeof useLocation>;
  onLogout: () => void;
}) {
  const drawerRef = useRef<HTMLDivElement>(null);
  const { loading, kind, prospect } = useTenantProfile();

  useEffect(() => {
    if (!loading && kind === null) {
      notifyTenantPortalAuthFailure('missing_portal_identity');
    }
  }, [loading, kind]);

  if (isPortalSessionInvalidating()) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <TenantAppearanceDock />
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-700 mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-400">Your session expired. Signing you out…</p>
        </div>
      </div>
    );
  }

  if (!loading && kind === null) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <TenantAppearanceDock />
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-700 mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-400">Loading your account…</p>
        </div>
      </div>
    );
  }

  const showTenantChrome = kind === 'tenant';
  const needsProspectDetails = kind === 'prospect' && !prospect?.profileComplete;
  const prospectSetupPaths = PROSPECT_PORTAL_PATHS;

  if (needsProspectDetails && !prospectSetupPaths.includes(location.pathname)) {
    return <Navigate to="/my-information" replace />;
  }

  if (
    !showTenantChrome &&
    (location.pathname === '/move-in' || location.pathname === '/move-out')
  ) {
    return <Navigate to={kind === 'prospect' ? '/my-information' : '/'} replace />;
  }

  const logoHome = kind === 'tenant' ? '/' : '/my-information';
  const currentProspectStep =
    kind === 'prospect' ? PROSPECT_PORTAL_STEPS.find((step) => step.href === location.pathname) : undefined;

  return (
    <div className="h-full flex flex-col overflow-hidden">
        <ProspectSidebarProgress />
        <PortalSessionHeartbeat />
        <WebsiteHiveProspectIntentSync />
        {/* Mobile/tablet backdrop */}
        <div
          className={cn(
            'fixed inset-0 z-40 bg-gray-900/80 transition-opacity duration-300 xl:hidden',
            sidebarOpen ? 'opacity-100' : 'opacity-0 pointer-events-none',
          )}
          aria-hidden="true"
          onClick={closeSidebar}
        />

        {/* Mobile/tablet drawer */}
        <div
          ref={drawerRef}
          className={cn(
            'fixed inset-y-0 left-0 z-50 w-72 transform transition-transform duration-300 ease-in-out xl:hidden',
            sidebarOpen ? 'translate-x-0' : '-translate-x-full',
          )}
        >
          <div className="flex h-full grow flex-col gap-y-5 overflow-y-auto bg-white dark:bg-gray-800 px-6 pb-2 shadow-2xl dark:bg-gray-900">
            <SidebarContent
              currentPath={location.pathname}
              onLogout={onLogout}
              onNavClick={closeSidebar}
              themeOptions="light-dark"
            />
          </div>
          <button
            type="button"
            onClick={closeSidebar}
            className={cn(
              'absolute left-full top-0 flex w-16 justify-center pt-5 transition-opacity duration-300',
              sidebarOpen ? 'opacity-100' : 'opacity-0 pointer-events-none',
            )}
          >
            <span className="sr-only">Close sidebar</span>
            <X size={24} className="text-white" />
          </button>
        </div>

        {/* Desktop sidebar — only at xl (1280px+) */}
        <div className="hidden xl:fixed xl:inset-y-0 xl:z-50 xl:flex xl:w-72 xl:flex-col">
          <div className="relative flex grow flex-col gap-y-5 overflow-y-auto border-r border-gray-200 bg-white px-6 dark:border-white/10 dark:bg-gray-900 dark:before:pointer-events-none dark:before:absolute dark:before:inset-0 dark:before:bg-black/10">
            <SidebarContent
              currentPath={location.pathname}
              onLogout={onLogout}
            />
          </div>
        </div>

        {/* Top bar — visible below xl */}
        <div className="sticky top-0 z-40 shrink-0 bg-red-700 shadow-sm xl:hidden">
          <div className="flex min-h-[3.25rem] items-center gap-x-4 px-4 pb-3 pt-[max(0.75rem,env(safe-area-inset-top))] sm:gap-x-6 sm:px-6 sm:pb-4">
            <button
              type="button"
              onClick={() => setSidebarOpen(true)}
              className="touch-optimized flex shrink-0 items-center justify-center rounded-md p-2 text-white hover:bg-white/10 hover:text-red-100"
            >
              <span className="sr-only">Open sidebar</span>
              <Menu size={24} />
            </button>
            <Link to={logoHome} className="flex shrink-0 items-center gap-2">
              <img src={logoMark} alt="Amber Peak" className="h-7 w-auto shrink-0 brightness-0 invert" />
            </Link>
            {currentProspectStep ? (
              <div className="min-w-0 flex-1 text-right">
                <p className="text-[10px] font-semibold uppercase tracking-wide text-red-200">{currentProspectStep.stepLabel}</p>
                <p className="truncate text-sm font-semibold leading-tight text-white">{currentProspectStep.name}</p>
              </div>
            ) : (
              <span className="flex-1" aria-hidden="true" />
            )}
          </div>
        </div>

        {/* Main content */}
        <main className="xl:pl-72 flex-1 overflow-y-auto min-h-0 bg-gray-100 dark:bg-gray-900">
          <div className={kind === 'prospect' ? 'py-6 pb-32 sm:pb-36' : 'py-6'}>
            <div className="px-2 sm:px-6 xl:px-8">
              {kind === 'prospect' && <ProspectDesktopLinkCard />}
              <Outlet />
            </div>
          </div>
        </main>

        {kind === 'prospect' && <ProspectNextSectionButton currentPath={location.pathname} />}
      </div>
  );
}

function TenantLayout() {
  const { isAuthenticated, isLoading } = useAuth0();
  const location = useLocation();
  const assistedToken = readAssistedLoginSessionToken();

  if (assistedToken) {
    return <MainLayoutInner sessionToken={assistedToken} />;
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <TenantAppearanceDock />
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-700 mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-400">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    if (location.pathname !== '/') {
      return <Navigate to="/" replace />;
    }
    return <LoginPage />;
  }

  return <MainLayoutInner />;
}

export default TenantLayout;
